import React, { useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import Collapse from "@mui/material/Collapse";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import {
  BRAND_INSIGHTS_UNREAD_ONE,
  BRAND_INSIGHTS_UNREAD_OTHER,
} from "../../i18n/keysTranslations";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Link, useLocation } from "react-router-dom";
import { getNameBusinessTheme } from "../../actions/getters";
import { setOpenSlideMenu } from "../../actions/ui";
import LinkText from "../Texts/LinkText";
import ButtonSlideMenu from "../Buttons/ButtonSlideMenu";
import { getPathDatabaseByRole } from "../../actions/auth";
import { useBrandInsightsUnread } from "../../hooks/useBrandInsightsUnread";
import { NewFeatureTooltip } from "../../features/newFeatures";
import BrandInsightsUnreadBadge from "./BrandInsightsUnreadBadge";
import { useIsSlideMenuCollapsed } from "./SlideMenuCollapsedContext";

const MenuSlideButton = ({ featureId, children, ...buttonProps }) => (
  <NewFeatureTooltip featureId={featureId}>
    <ButtonSlideMenu {...buttonProps}>{children}</ButtonSlideMenu>
  </NewFeatureTooltip>
);

const MenuGroupItem = ({ menu, onTabletLinkClick }) => {
  const [open, setOpen] = useState(true);

  const theme = useTheme();
  const dispatch = useDispatchApp();
  const isTablet = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const slideMenuIsOpen = useSelectorApp((state) => state.ui.slideMenuIsOpen);
  const { role, agencyID } = dispatch(getPathDatabaseByRole());
  const { t } = useTranslationApp();
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const { newCount } = useBrandInsightsUnread();
  const { pathname } = useLocation();
  const isCollapsed = useIsSlideMenuCollapsed();

  const onDrawerToggle = () => {
    dispatch(setOpenSlideMenu(!slideMenuIsOpen));
  };

  const onTabletNav = () => {
    if (!isTablet) return;
    (onTabletLinkClick ?? onDrawerToggle)();
  };

  const onClick = () => {
    setOpen(!open);
  };

  const sxContainerButtonMenu = {
    display: "flex",
    alignItems: "center",
  };
  return (
    <Box sx={{ mx: 2 }}>
      {menu.roles.includes(role) && menu.name && !isCollapsed ? (
        <Typography
          variant="body2"
          color={theme.palette.background.paperContrastText}
          sx={{ ml: 1, fontWeight: 300 }}
        >
          {t(menu.name)}
        </Typography>
      ) : null}
      <List
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {menu.subMenus.map((subMenu) => {
          if (!subMenu.roles.includes(role)) return null;

          if (subMenu.hasSubMenus) {
            return (
              <Box key={subMenu.nameKey}>
                <MenuSlideButton
                  featureId={subMenu.newFeatureId}
                  id={`menu-${subMenu.nameKey}`}
                  startIcon={subMenu.icon}
                  endIcon={open ? <ExpandLess /> : <ExpandMore />}
                  onClick={onClick}
                >
                  {t(subMenu.name, { NAME_BUSINESS })}
                </MenuSlideButton>
                <Collapse in={open} timeout="auto" unmountOnExit>
                  {subMenu.subMenus.map((subSubMenu) => {
                    if (subSubMenu.customComponent) {
                      return (
                        <Box
                          key={subSubMenu.nameKey}
                          sx={{
                            pl: isCollapsed ? 0 : 2,
                            mb: 1,
                          }}
                        >
                          {React.cloneElement(subSubMenu.customComponent, {
                            onTabletLinkClick: onTabletNav,
                          })}
                        </Box>
                      );
                    }
                    return (
                      <Link
                        to={subSubMenu.link}
                        key={subSubMenu.nameKey}
                        onClick={onTabletNav}
                        style={{
                          ...sxContainerButtonMenu,
                          textDecoration: "none",
                          paddingLeft: isCollapsed ? 0 : "16px",
                        }}
                      >
                        <MenuSlideButton
                          featureId={subSubMenu.newFeatureId}
                          id={`menu-${subSubMenu.nameKey}`}
                          menu={subSubMenu}
                          startIcon={subSubMenu.icon}
                        >
                          {t(subSubMenu.name)}
                        </MenuSlideButton>
                      </Link>
                    );
                  })}
                </Collapse>
              </Box>
            );
          }

          if (subMenu.isAgencyMenu && !agencyID) return null;

          if (subMenu.isRedirect)
            return (
              <LinkText
                href={subMenu.link}
                target="_blank"
                key={subMenu.nameKey}
                onClick={onTabletNav}
                sx={{ ...sxContainerButtonMenu, textDecoration: "none" }}
              >
                <MenuSlideButton
                  featureId={subMenu.newFeatureId}
                  id={`menu-${subMenu.nameKey}`}
                  menu={subMenu}
                  startIcon={subMenu.icon}
                >
                  {t(subMenu.name, { NAME_BUSINESS })}
                </MenuSlideButton>
              </LinkText>
            );

          if (subMenu.customComponent)
            return (
              <Box key={subMenu.nameKey}>
                {React.cloneElement(subMenu.customComponent, {
                  onTabletLinkClick: onTabletNav,
                })}
              </Box>
            );
          const showUnread = Boolean(
            subMenu.showUnreadBadge && newCount > 0 && pathname !== subMenu.link
          );
          const itemLabel = t(subMenu.name, { NAME_BUSINESS });
          const unreadLabel = showUnread
            ? newCount === 1
              ? t(BRAND_INSIGHTS_UNREAD_ONE)
              : t(BRAND_INSIGHTS_UNREAD_OTHER, {
                  count: newCount > 99 ? "99+" : newCount,
                })
            : "";

          return (
            <Link
              to={subMenu.link}
              key={subMenu.nameKey}
              onClick={onTabletNav}
              style={{ ...sxContainerButtonMenu, textDecoration: "none" }}
            >
              <MenuSlideButton
                featureId={subMenu.newFeatureId}
                id={`menu-${subMenu.nameKey}`}
                menu={subMenu}
                startIcon={subMenu.icon}
                endIcon={
                  showUnread ? (
                    <BrandInsightsUnreadBadge count={newCount} />
                  ) : undefined
                }
                collapsedIndicator={showUnread}
                ariaLabel={
                  isCollapsed && showUnread
                    ? `${itemLabel}, ${unreadLabel}`
                    : undefined
                }
              >
                {itemLabel}
              </MenuSlideButton>
            </Link>
          );
        })}
      </List>
    </Box>
  );
};

export default MenuGroupItem;
